import { useCallback, useEffect, useMemo, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import useAuthContext from '../../apps/common/modules/auth/hooks/useAuthContext';
import StorageService from '../../Services/storage/storage.service';
import Constants from '../../utils/Constants';
import { INavFeedlot } from '../../utils/Interface/NavInterface';
import { useAppDispatch, useAppSelector } from '../../Redux/Store';
import { setFeedlotToUpdate } from '../../Redux/Reducer/common';
import { FeedlotManagerModelsResponseModelsFeedlotApiResponse } from '../../Redux/Apis/FMFeedlot/baseFMFeedlotApi';
import { authActions } from '../../apps/common/modules/auth/authSlice';
import { apiCall_v2 } from '../../Services/AxiosService';

export default function FeedlotDropdown() {
    const dispatch = useAppDispatch();
    const [usersFeedlots, setUsersFeedlots] = useState<
        Required<FeedlotManagerModelsResponseModelsFeedlotApiResponse>[]
    >([]);
    const { selectedFeedlot } = useAppSelector(state => state.common);
    const storage = useMemo(() => new StorageService(), []);
    const { getUsersFeedlots, getAccessToken, getLocalStorageKey } =
        useAuthContext();

    const updateSelectedFeedlot = useCallback(
        async (
            feedlotDetail: Required<FeedlotManagerModelsResponseModelsFeedlotApiResponse>,
            newFeedlotSelected = false,
        ) => {
            storage.setValue(
                Constants.StorageItems.SELECTED_FEEDLOT,
                feedlotDetail,
                Constants.Storage.LOCAL,
            );
            if (newFeedlotSelected) {
                window.location.reload();
            } else {
                dispatch(setFeedlotToUpdate(feedlotDetail));
            }
        },
        [dispatch, storage],
    );

    useEffect(() => {
        const updateFeedlotDropdownAndSelection = async () => {
            dispatch(
                authActions.setAccessToken({
                    accessToken: getAccessToken(),
                    storageKey: getLocalStorageKey(),
                }),
            );
            const allFeedlots: Required<FeedlotManagerModelsResponseModelsFeedlotApiResponse>[] =
                await apiCall_v2({
                    method: 'get',
                    url: `${Constants.apiUrls.FEED_LOT_MANAGER}${Constants.apiUrls.FM_FEEDLOT}`,
                });

            const usersFeedlots = getUsersFeedlots(allFeedlots).sort((a, b) => {
                if (a.label && b.label) {
                    return a.label > b.label ? 1 : -1;
                } else {
                    return 0;
                }
            });
            setUsersFeedlots(usersFeedlots);

            const selectedFeedlotFromStorage: INavFeedlot = storage.getValue(
                Constants.StorageItems.SELECTED_FEEDLOT,
            );
            const matchingFeedlot = usersFeedlots.find(
                usersFeedlot =>
                    usersFeedlot.id === selectedFeedlotFromStorage?.id,
            );
            await updateSelectedFeedlot(
                matchingFeedlot ?? usersFeedlots?.[0] ?? {},
            );
        };
        updateFeedlotDropdownAndSelection();
    }, [
        dispatch,
        getAccessToken,
        getLocalStorageKey,
        getUsersFeedlots,
        storage,
        updateSelectedFeedlot,
    ]);

    return (
        <div className="mx-3">
            <Dropdown className="top-header-dropdown">
                {usersFeedlots.length > 1 ? (
                    <>
                        <Dropdown.Toggle className="p-0 d-flex align-items-center">
                            {selectedFeedlot?.labelColorHex && (
                                <div
                                    className="colored-circle mb-1 me-2"
                                    style={{
                                        backgroundColor:
                                            '#' +
                                            selectedFeedlot?.labelColorHex,
                                    }}
                                ></div>
                            )}
                            {selectedFeedlot.label || ''}
                        </Dropdown.Toggle>
                        <Dropdown.Menu>
                            {usersFeedlots.map((feedlot, index) => (
                                <Dropdown.Item
                                    key={feedlot.id}
                                    onClick={async () => {
                                        await updateSelectedFeedlot(
                                            feedlot,
                                            true,
                                        );
                                    }}
                                    className="p-0 px-1 d-flex align-items-center"
                                >
                                    {feedlot.labelColorHex ? (
                                        <div
                                            className="colored-circle mb-1 me-2"
                                            style={{
                                                backgroundColor:
                                                    '#' + feedlot.labelColorHex,
                                            }}
                                        ></div>
                                    ) : (
                                        <div className="ms-4"></div>
                                    )}
                                    {feedlot.label}
                                </Dropdown.Item>
                            ))}
                        </Dropdown.Menu>
                    </>
                ) : (
                    <Dropdown.Toggle
                        className="cursor-default p-0 d-flex align-items-center"
                        bsPrefix="p-0"
                    >
                        {selectedFeedlot?.labelColorHex && (
                            <div
                                className="colored-circle mb-1 me-1"
                                style={{
                                    backgroundColor:
                                        '#' + selectedFeedlot?.labelColorHex,
                                }}
                            ></div>
                        )}
                        {selectedFeedlot.label || ''}
                    </Dropdown.Toggle>
                )}
            </Dropdown>
        </div>
    );
}
